import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  CONTENT_INNER_PX_SIZE,
  PageLayout,
  SIDEBAR_ID,
} from '@/components/templates/PageLayout';
import {
  CmBox,
  CmCard,
  CmCheckBox,
  CmFixedTd,
  CmFlex,
  CmLabelAndInput,
  CmLabelAndSingleReactSelect,
  CmScrollBox,
  CmTable,
  CmTableBodyRow,
  CmTableHeader,
  CmTabPanel,
  CmTabs,
  CmTbody,
  CmTd,
  CmTh,
  CmThWithQuestionIcon,
  CmTooltip,
  ReactSingleSelectInput,
} from '@/components/uiParts';
import { useTargetsDomHeight, useTargetsDomWidth } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import {
  AuthStatusSearchType,
  authStatusSearchTypeMap,
  authTableThs,
  IntegrationStatusSearchType,
  integrationStatusSearchTypeMap,
  statusUpdateTableThs,
} from '@/pages/store-management/uber-stores/activate/service';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  UberStoreListResponse,
  useStoreManagementUberStoreList,
} from '@/services/StoreManagement/UberStores/hooks';

import { CreationConfirmModal } from './components/CreationConfirmModal';
import { OrderManagerToggleAction } from './components/OrderManagerToggleAction';

export const SEARCH_AREA_ID = 'search-form';

// 未認証店舗の判定はフィルタと同じ linked_status を判定軸にする。
// is_order_manager は注文連携 ON/OFF を表すフィールドで、認証/連携の有無は
// linked_status (null/"enabled") が真の状態を持つ。バックエンド仕様では
// 両者は同期する想定だが、フロントの型上は独立なフィールドのため、
// 認証/連携の有無判定はすべて linked_status に統一する。
const isNotAuthenticated = (store: UberStoreListResponse) =>
  store.linked_status === null;

// タブ index の意味付け。0=店舗認証, 1=連携ステータス更新（デフォルトは店舗認証）
const TAB_INDEX = {
  AUTHENTICATE: 0,
  UPDATE_STATUS: 1,
} as const;
const tabNames = ['店舗認証', '連携ステータス更新'];

const StoreManagementUberStoresActivatePage: React.FC = (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const { managerAccountMe } = useManagerAccountMe();
  redirect404Page('store_management', history, managerAccountMe);

  const { targetsHeight } = useTargetsDomHeight([SEARCH_AREA_ID]);
  const { targetsWidth: sidebarWidth } = useTargetsDomWidth([SIDEBAR_ID]);

  const [uberStores, setUberStores] = useState<UberStoreListResponse[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    TAB_INDEX.AUTHENTICATE
  );

  // 店舗認証タブ用: 個別選択（認証済店舗も再認証目的で選択可能）。
  // 全選択チェックは selectedUberStoreIDs / authFilteredStores から派生で算出する
  // （isAllCheck を state で持つと、フィルタ変更や個別解除と表示が乖離する）。
  const [selectedUberStoreIDs, setSelectedUberStoreIDs] = useState<string[]>(
    []
  );

  // 検索キーワードもタブごとに独立。UI 配置がタブパネル内のため、
  // 状態だけ共有すると「見た目はタブごと、値は同期」という UX 上の誤認を生むため。
  const [authKeyword, setAuthKeyword] = useState<string>('');
  const handleInputAuthKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAuthKeyword(e.target.value);
  };
  const [statusUpdateKeyword, setStatusUpdateKeyword] = useState<string>('');
  const handleInputStatusUpdateKeyword = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStatusUpdateKeyword(e.target.value);
  };

  // 店舗ステータス別検索の選択値はタブごとに独立。
  // 店舗認証タブは認証ステータス基準、連携ステータス更新タブは注文連携ステータス基準で意味が異なる。
  const [selectedAuthStatusSearchType, setSelectedAuthStatusSearchType] =
    useState<AuthStatusSearchType>('UNSPECIFIED');
  const [
    selectedIntegrationStatusSearchType,
    setSelectedIntegrationStatusSearchType,
  ] = useState<IntegrationStatusSearchType>('UNSPECIFIED');

  const handleAuthStatusSearchType = (
    value: string
  ): ReactSingleSelectInput => {
    return {
      value: authStatusSearchTypeMap.find((item) => item.value === value),
      options: authStatusSearchTypeMap,
      onChange: (selected) => {
        if (selected) {
          setSelectedAuthStatusSearchType(
            selected.value as AuthStatusSearchType
          );
        }
      },
      onReset: () => {
        return;
      },
    };
  };

  const handleIntegrationStatusSearchType = (
    value: string
  ): ReactSingleSelectInput => {
    return {
      value: integrationStatusSearchTypeMap.find(
        (item) => item.value === value
      ),
      options: integrationStatusSearchTypeMap,
      onChange: (selected) => {
        if (selected) {
          setSelectedIntegrationStatusSearchType(
            selected.value as IntegrationStatusSearchType
          );
        }
      },
      onReset: () => {
        return;
      },
    };
  };

  // 両タブの店舗ステータス別検索は linked_status (null/"enabled") を判定軸として共有する。
  // タブごとに語彙（認証 vs 連携）が異なるためラベルと選択値の型は分けているが、
  // 内部的には同じ二分の絞り込みを行う。
  // 店舗認証タブ用：キーワード + linked_status で絞り込み
  const authFilteredStores = useMemo(() => {
    return uberStores
      .filter((store) => authKeyword === '' || store.name.includes(authKeyword))
      .filter((store) => {
        switch (selectedAuthStatusSearchType) {
          case 'NOT_AUTHENTICATED':
            return store.linked_status === null;
          case 'AUTHENTICATED':
            return store.linked_status !== null;
          default:
            return true;
        }
      });
  }, [uberStores, authKeyword, selectedAuthStatusSearchType]);

  // 検索キーワード・店舗ステータス別検索の変更時は選択状態をリセットする。
  // 表示外に隠れた選択が CreationConfirmModal の認証対象に残ると、
  // ユーザーが視認していない店舗まで認証/再認証されてしまうため。
  useEffect(() => {
    setSelectedUberStoreIDs([]);
  }, [authKeyword, selectedAuthStatusSearchType]);

  // 連携ステータス更新タブ用：キーワード + linked_status で絞り込み
  const statusUpdateFilteredStores = useMemo(() => {
    return uberStores
      .filter(
        (store) =>
          statusUpdateKeyword === '' || store.name.includes(statusUpdateKeyword)
      )
      .filter((store) => {
        switch (selectedIntegrationStatusSearchType) {
          case 'LINKED':
            return store.linked_status !== null;
          case 'NOT_LINKED':
            return store.linked_status === null;
          default:
            return true;
        }
      });
  }, [uberStores, statusUpdateKeyword, selectedIntegrationStatusSearchType]);

  const handleTabsChange = (index: number) => {
    setActiveTabIndex(index);
  };

  // 各行のチェックボックス（店舗認証タブのみ）
  // 認証済店舗も再認証のために選択可能とする。
  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id;
    const checked = e.target.checked;
    if (checked) {
      setSelectedUberStoreIDs([...selectedUberStoreIDs, id]);
    } else {
      setSelectedUberStoreIDs(
        selectedUberStoreIDs.filter((item) => item !== id)
      );
    }
  };

  const handleAllCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedUberStoreIDs(authFilteredStores.map((store) => store.id));
    } else {
      setSelectedUberStoreIDs([]);
    }
  };

  // 全選択チェックは「表示中の全店舗が選択済みか」から派生させる。
  // フィルタ変更時の選択リセット (上の useEffect) と組み合わせて、
  // 「表示と選択は常に同期している」という不変条件を保つ。
  const isAllCheck =
    authFilteredStores.length > 0 &&
    authFilteredStores.every((store) =>
      selectedUberStoreIDs.includes(store.id)
    );

  // 認証済店舗の is_order_manager 切替成功時にローカル state を即座に反映
  const handleOrderManagerUpdated = (
    storeId: string,
    isOrderManager: boolean
  ) => {
    setUberStores((stores) =>
      stores.map((store) =>
        store.id === storeId
          ? { ...store, is_order_manager: isOrderManager }
          : store
      )
    );
  };

  const isDisabledAuthButton = selectedUberStoreIDs.length === 0;

  // 初回遷移時のデータ取得
  const errMsg =
    'UberEats店舗一覧を取得できませんでした。店舗一覧の「UberEats一括認証」ボタンから再度サインインしてください。';
  useEffect(() => {
    (async () => {
      const queryParams = new URLSearchParams(location.search);
      const code = queryParams.get('code');
      if (!code) {
        alert(errMsg);
        history.push(routes.storeManagementMasterStores.path);
        return;
      }
      try {
        const fetched = await useStoreManagementUberStoreList(code);
        setUberStores(fetched);
      } catch (error) {
        if (
          error instanceof HttpError &&
          error.status >= 400 &&
          error.status < 500
        ) {
          alert(errMsg);
          history.push(routes.storeManagementMasterStores.path);
          return;
        }
        // @ts-ignore TS2322
        return errorHandler({ error });
      }
    })();
  }, [location]);

  const cardHeight = `calc(100vh - ${targetsHeight}px - 20px)`;
  const cardWidth = `calc(100vw - ${
    sidebarWidth + CONTENT_INNER_PX_SIZE * 2
  }px)`;

  // 検索/フィルタ + 認証ボタン群（店舗認証タブ）
  const authTabControls = (
    <CmBox padding="20px 20px 20px 0">
      <CmFlex alignItems="flex-end" w="full">
        <CmBox width="30%">
          <CmLabelAndInput
            bg="white"
            id="name-auth"
            label="Uber Eats出店店舗名検索"
            fontWeight="bold"
            value={authKeyword}
            onChange={handleInputAuthKeyword}
            height="38px"
          />
        </CmBox>
        <CmBox width="30%" paddingLeft={2}>
          <CmLabelAndSingleReactSelect
            label="店舗ステータス別検索"
            name="authStatus"
            fontWeight="bold"
            closeMenuOnSelect
            isClearable={false}
            singleSelectInput={handleAuthStatusSearchType(
              selectedAuthStatusSearchType
            )}
          />
        </CmBox>
        <CmFlex width="40%" justifyContent="flex-end">
          <CreationConfirmModal
            selectedUberStoreIDs={selectedUberStoreIDs}
            uberStores={uberStores}
            isDisabledAuthButton={isDisabledAuthButton}
            isOrderManager={false}
          />
          <CreationConfirmModal
            selectedUberStoreIDs={selectedUberStoreIDs}
            uberStores={uberStores}
            isDisabledAuthButton={isDisabledAuthButton}
            isOrderManager={true}
          />
        </CmFlex>
      </CmFlex>
    </CmBox>
  );

  // 検索/フィルタのみ（連携ステータス更新タブ）
  const statusUpdateTabControls = (
    <CmBox padding="20px 20px 20px 0">
      <CmFlex alignItems="flex-end" w="full">
        <CmBox width="50%">
          <CmLabelAndInput
            bg="white"
            id="name-status-update"
            label="Uber Eats出店店舗名検索"
            fontWeight="bold"
            value={statusUpdateKeyword}
            onChange={handleInputStatusUpdateKeyword}
            height="38px"
          />
        </CmBox>
        <CmBox width="40%" paddingLeft={2}>
          <CmLabelAndSingleReactSelect
            label="店舗ステータス別検索"
            name="integrationStatus"
            fontWeight="bold"
            closeMenuOnSelect
            isClearable={false}
            singleSelectInput={handleIntegrationStatusSearchType(
              selectedIntegrationStatusSearchType
            )}
          />
        </CmBox>
      </CmFlex>
    </CmBox>
  );

  // 店舗認証タブのテーブル
  const authTable = (
    <CmTable variant="unstyled">
      <CmTableHeader>
        {authTableThs.map((th) => {
          if (th.label === '全選択') {
            return (
              <CmTh
                key={th.label}
                minWidth={th.minWidth}
                left="0"
                zIndex="2"
                backgroundColor="#EDF2F7"
                borderBottom="1px solid #A0AEC0"
              >
                <CmCheckBox
                  onChange={handleAllCheck}
                  isChecked={isAllCheck}
                  isDisabled={authFilteredStores.length === 0}
                  marginRight="1"
                  borderColor="#A0AEC0"
                  backgroundColor="#FFFFFF"
                />
                {th.label}
              </CmTh>
            );
          }
          const style = {
            minWidth: th.minWidth,
            textAlign: 'left',
            backgroundColor: '#EDF2F7',
            borderLeft: '1px solid #A0AEC0',
            borderBottom: '1px solid #A0AEC0',
          } as const;
          return (
            <CmThWithQuestionIcon
              key={th.label}
              label={th.label}
              {...style}
              question={th.question}
            />
          );
        })}
      </CmTableHeader>
      <CmTbody>
        {authFilteredStores.map((uberStore, index) => {
          const notAuthenticated = isNotAuthenticated(uberStore);
          return (
            <CmTableBodyRow key={index + 1}>
              <CmFixedTd
                {...{
                  left: '0',
                  zIndex: '0',
                  width: '10%',
                  borderBottom: '1px solid #A0AEC0',
                }}
              >
                <CmBox tabIndex={0} cursor="pointer" padding="5px">
                  <CmCheckBox
                    id={uberStore.id}
                    isChecked={selectedUberStoreIDs.includes(uberStore.id)}
                    onChange={handleCheck}
                    borderColor="#A0AEC0"
                  />
                </CmBox>
              </CmFixedTd>
              <CmTd
                textAlign="left"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                <CmTooltip label={uberStore.name}>{uberStore.name}</CmTooltip>
              </CmTd>
              <CmTd
                textAlign="left"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                {uberStore.master_store?.name && (
                  <CmTooltip label={uberStore.master_store?.name}>
                    {uberStore.master_store?.name}
                  </CmTooltip>
                )}
              </CmTd>
              <CmTd
                textAlign="left"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                {uberStore.auth_store?.name && (
                  <CmTooltip label={uberStore.auth_store?.name}>
                    {uberStore.auth_store?.name}
                  </CmTooltip>
                )}
              </CmTd>
              <CmTd
                textAlign="center"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                {notAuthenticated ? '未認証' : '認証済'}
              </CmTd>
            </CmTableBodyRow>
          );
        })}
      </CmTbody>
    </CmTable>
  );

  // 連携ステータス更新タブのテーブル
  const statusUpdateTable = (
    <CmTable variant="unstyled">
      <CmTableHeader>
        {statusUpdateTableThs.map((th) => {
          const style = {
            minWidth: th.minWidth,
            textAlign: 'left',
            backgroundColor: '#EDF2F7',
            borderLeft: '1px solid #A0AEC0',
            borderBottom: '1px solid #A0AEC0',
          } as const;
          return (
            <CmThWithQuestionIcon
              key={th.label}
              label={th.label}
              {...style}
              question={th.question}
            />
          );
        })}
      </CmTableHeader>
      <CmTbody>
        {statusUpdateFilteredStores.map((uberStore, index) => {
          const notAuthenticated = isNotAuthenticated(uberStore);
          return (
            <CmTableBodyRow key={index + 1}>
              <CmTd
                textAlign="left"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                <CmTooltip label={uberStore.name}>{uberStore.name}</CmTooltip>
              </CmTd>
              <CmTd
                textAlign="left"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                {uberStore.master_store?.name && (
                  <CmTooltip label={uberStore.master_store?.name}>
                    {uberStore.master_store?.name}
                  </CmTooltip>
                )}
              </CmTd>
              <CmTd
                textAlign="left"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                {uberStore.auth_store?.name && (
                  <CmTooltip label={uberStore.auth_store?.name}>
                    {uberStore.auth_store?.name}
                  </CmTooltip>
                )}
              </CmTd>
              <CmTd
                textAlign="center"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                {uberStore.is_order_manager === true ? (
                  <CmTooltip label="連携中">連携中</CmTooltip>
                ) : (
                  '-'
                )}
              </CmTd>
              <CmTd
                textAlign="center"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                {notAuthenticated ? (
                  '-'
                ) : (
                  <CmTooltip label="連携中">連携中</CmTooltip>
                )}
              </CmTd>
              <CmTd
                textAlign="center"
                borderLeft="1px solid #A0AEC0"
                borderBottom="1px solid #A0AEC0"
              >
                {notAuthenticated ? (
                  '-'
                ) : (
                  <OrderManagerToggleAction
                    uberStore={uberStore}
                    onUpdated={handleOrderManagerUpdated}
                  />
                )}
              </CmTd>
            </CmTableBodyRow>
          );
        })}
      </CmTbody>
    </CmTable>
  );

  return (
    <PageLayout title={routes.storeManagementUberStoresActivate.title}>
      {/*
        SEARCH_AREA_ID は CmCard の高さ計算（cardHeight）の差し引き対象になる。
        CmCard をこの ID 配下に置くと「CmCard 高さ変化 → 親の高さ変化 → 再計算 → CmCard 再リサイズ」
        の無限ループになるため、CmCard は必ずこの要素の外に置くこと。
      */}
      <CmBox id={SEARCH_AREA_ID} pt="20px">
        <CmTabs
          tabNames={tabNames}
          index={activeTabIndex}
          onChange={handleTabsChange}
        >
          <CmTabPanel>{authTabControls}</CmTabPanel>
          <CmTabPanel>{statusUpdateTabControls}</CmTabPanel>
        </CmTabs>
      </CmBox>

      <CmCard height={cardHeight} w={cardWidth} padding="0px" borderRadius="sm">
        <CmScrollBox>
          {activeTabIndex === TAB_INDEX.AUTHENTICATE
            ? authTable
            : statusUpdateTable}
        </CmScrollBox>
      </CmCard>
    </PageLayout>
  );
};

export default StoreManagementUberStoresActivatePage;
